import type { Track } from '../types/music'

const ENDPOINT = 'https://itunes.apple.com/search'

interface RawResult {
  trackId?: number
  trackName?: string
  artistName?: string
  collectionName?: string
  artworkUrl100?: string
  previewUrl?: string
  trackTimeMillis?: number
  primaryGenreName?: string
}

function upscaleArtwork(url: string, size: number): string {
  return url.replace(/\/\d+x\d+bb\./, `/${size}x${size}bb.`).replace('100x100', `${size}x${size}`)
}

function toTrack(raw: RawResult, index: number): Track | null {
  if (!raw.previewUrl || !raw.trackName || !raw.artistName) return null
  const art = raw.artworkUrl100 ?? ''
  return {
    id: String(raw.trackId ?? `${raw.trackName}-${index}`),
    title: raw.trackName,
    artist: raw.artistName,
    album: raw.collectionName ?? 'Single',
    artwork: art ? upscaleArtwork(art, 600) : '',
    artworkSmall: art ? upscaleArtwork(art, 200) : '',
    previewUrl: raw.previewUrl,
    durationMs: raw.trackTimeMillis ?? 0,
    genre: raw.primaryGenreName ?? '',
  }
}

export async function searchTracks(
  term: string,
  limit = 25,
  signal?: AbortSignal,
): Promise<Track[]> {
  const url = `${ENDPOINT}?term=${encodeURIComponent(term)}&media=music&entity=song&limit=${limit}`
  const res = await fetch(url, { signal })
  if (!res.ok) throw new Error(`iTunes request failed (${res.status})`)
  const json = (await res.json()) as { results?: RawResult[] }
  const seen = new Set<string>()
  const tracks: Track[] = []
  ;(json.results ?? []).forEach((raw, i) => {
    const track = toTrack(raw, i)
    if (!track) return
    const key = `${track.title.toLowerCase()}|${track.artist.toLowerCase()}`
    if (seen.has(key)) return
    seen.add(key)
    tracks.push(track)
  })
  return tracks
}
